import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tent, Loader2 } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1779078063864-907abe46e955?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxjYXJhdmFuJTIwcGFyayUyMG5hdHVyZXxlbnwwfHx8fDE3ODYzMTk4Njh8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome back, ${data.name}`);
      navigate(data.role === "admin" ? "/admin" : "/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Unable to sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      <div className="relative hidden md:block">
        <img src={HERO} alt="Shady Acres Caravan Park" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/45" />
        <div className="absolute bottom-0 left-0 p-10 text-white">
          <h2 className="max-w-sm font-heading text-3xl font-extrabold leading-tight">
            Training that keeps our park safe and welcoming.
          </h2>
          <p className="mt-3 max-w-sm text-white/85">
            Complete your modules, pass the quick quizzes, and keep your record up to date.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="w-full max-w-sm"
        >
          <div className="mb-8 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
              <Tent className="h-6 w-6" strokeWidth={2.2} />
            </span>
            <div className="leading-tight">
              <p className="font-heading text-lg font-extrabold tracking-tight">Shady Acres</p>
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Staff Training Portal</p>
            </div>
          </div>

          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">Use the login your manager set up for you.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@shadyacrescaravanpark.com.au"
                className="h-12 rounded-xl bg-card"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-xl bg-card"
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="h-12 w-full rounded-xl text-base font-semibold hover:bg-primary/90 active:scale-[0.98]"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
